<template>
  <v-container v-if="triggerHistory" id="trigger-history" fluid>
    <v-card outlined>
      <v-tabs grow>
        <v-tab>{{ $t('triggersHistories.generalInformations') }}</v-tab>
        <v-tab :disabled="!sentDataBody && !sentAdditionalData">{{ $t('triggersHistories.sentData') }}</v-tab>
        <v-tab-item>
          <v-container fluid>
            <trigger-history-header :triggerHistory="triggerHistory" />
            <v-divider />
            <span v-if="triggerHistory.trigger && triggerHistory.trigger.trigger_calls">
              <v-card-text v-if="isMobile">{{ $t('triggersHistories.calls') }}:</v-card-text>
              <v-card-title v-else>{{ $t('triggersHistories.calls') }}:</v-card-title>
              <trigger-history-call
                v-for="call of triggerHistory.trigger.trigger_calls"
                v-bind:key="call._id"
                :call="call"
                :executionsErrors="getCallErrorHistory(call._id)"
              />
            </span>
            <v-row class="mt-2" justify="center">
              <v-btn
                v-if="callsEligibleForRelaunch"
                class="ma-2 mb-4 text-uppercase"
                color="primary"
                outlined
                :loading="loadingData"
                :disabled="loadingData || !callsToRelaunch.length"
                @click="confirmationRetryTriggerDialog = true"
              >
                {{ $t('triggersHistories.relaunch') }}
              </v-btn>
            </v-row>
          </v-container>
        </v-tab-item>
        <v-tab-item>
          <v-container fluid>
            <v-card outlined v-if="sentDataBody">
              <v-container fluid>
                <v-card-text>{{ $t('triggersHistories.sentEntity') }}</v-card-text>
                <v-textarea
                  :class="isDark ? 'triggerHistoryTextAreaDark' : 'triggerHistoryTextArea'"
                  v-model="sentDataBody"
                  dense
                  readonly
                  flat
                  rows="14"
                  hide-details="auto"
                />
              </v-container>
            </v-card>
            <v-card outlined class="mt-2" v-if="sentAdditionalData">
              <v-container fluid>
                <v-card-text>{{ $t('triggersHistories.additionalData') }}</v-card-text>
                <v-textarea
                  :class="isDark ? 'triggerHistoryTextAreaDark' : 'triggerHistoryTextArea'"
                  v-model="sentAdditionalData"
                  dense
                  readonly
                  flat
                  rows="5"
                  hide-details="auto"
                />
              </v-container>
            </v-card>
          </v-container>
        </v-tab-item>
      </v-tabs>
    </v-card>
    <errors-list-dialog
      v-model="listDialog"
      :items="targetedErrors"
      @close="listDialog = false"
      :headers="errorHeaders"
    />
    <confirmation-retry-trigger
      v-model="confirmationRetryTriggerDialog"
      :entity-type="triggerHistory.source_entity.entity_type"
      @close="confirmationRetryTriggerDialog = false"
      @confirm="relaunch"
    ></confirmation-retry-trigger>
  </v-container>
</template>

<script>
  import { EventBus } from '../../event-bus'
  import listDialog from '../../components/dialogs/ListDialog.vue'
  import { mapState } from 'vuex'
  import triggerHistoryHeader from '../../components/triggersHistories/TriggerHistoryHeader.vue'
  import triggerHistoryCall from '../../components/triggersHistories/TriggerHistoryCall.vue'
  import confirmationRetryTrigger from '@/components/dialogs/ConfirmationRetryTrigger'

  export default {
    name: 'TriggerHistoryDetails',
    components: {
      errorsListDialog: listDialog,
      triggerHistoryHeader: triggerHistoryHeader,
      triggerHistoryCall: triggerHistoryCall,
      confirmationRetryTrigger: confirmationRetryTrigger,
    },
    data() {
      return {
        loadingData: false,
        triggerHistory: undefined,
        targetedErrors: [],
        listDialog: false,
        checkConditions: false,
        getEntityToRetry: false,
        errorHeaders: [
          {
            text: this.$t('triggersHistories.errorCode'),
            value: 'code',
            sortable: true,
          },
          {
            text: this.$t('triggersHistories.date'),
            value: 'date',
            sortable: true,
          },
          {
            text: this.$t('triggersHistories.errorMessage'),
            value: 'message',
            sortable: true,
          },
          {
            text: '',
            value: 'action',
            sortable: false,
          },
        ],
        confirmationRetryTriggerDialog: false,
      }
    },
    async mounted() {
      await this.getTriggerHistory()
    },
    methods: {
      async getTriggerHistory() {
        this.loadingData = true

        let myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
        }
        try {
          this.triggerHistory = await this.$Amplify.API.get(
            'triggers',
            `/v1/int/triggers-histories/${this.$route.params.id}`,
            myInit
          )

          if (this.triggerHistory.trigger && Array.isArray(this.triggerHistory.trigger.trigger_calls)) {
            for (const call of this.triggerHistory.trigger.trigger_calls) {
              this.$set(call, 'retry', false)
            }
          }
        } catch (e) {
          console.log(e)

          EventBus.$emit('displaySnackbar', {
            display: true,
            text: this.$t('triggersHistories.errorTriggerHistoryLoading'),
            color: 'error',
          })
        } finally {
          this.loadingData = false
        }
      },
      applyOptions(options) {
        if (options?.checkConditions !== undefined) {
          this.checkConditions = options.checkConditions
        }
        if (options?.getEntityToRetry !== undefined) {
          this.getEntityToRetry = options.getEntityToRetry
        }
      },
      async relaunch(options) {
        this.loadingData = true
        this.confirmationRetryTriggerDialog = false
        this.applyOptions(options)
        let myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
          body: {
            trigger_id: this.triggerHistory.trigger.trigger_id,
            trigger_history_id: this.triggerHistory._id,
            check_conditions: this.checkConditions,
            calls: this.callsToRelaunch,
          },
        }
        if (this.getEntityToRetry && this.triggerHistory.source_entity) {
          // adding source_entity to the data indicates to the API to get the entity from DB
          myInit.body.source_entity = this.triggerHistory.source_entity
        }
        if (this.triggerHistory.sent_data?.body) {
          try {
            myInit.body.entity = JSON.parse(this.triggerHistory.sent_data.body)
          } catch {
            myInit.body.entity = this.triggerHistory.sent_data.body
          }
        }
        if (this.triggerHistory.sent_data?.additional_data) {
          try {
            myInit.body.additionalData = JSON.parse(this.triggerHistory.sent_data.additional_data)
          } catch {
            myInit.body.additionalData = this.triggerHistory.sent_data.additional_data
          }
        }
        try {
          const res = await this.$Amplify.API.post('triggers', '/v1/int/retry', myInit)

          this.triggerHistory = JSON.parse(res.body)

          EventBus.$emit('displaySnackbar', {
            display: true,
            text: this.$t('triggersHistories.triggerRelaunched'),
            color: 'success',
          })
        } catch (e) {
          console.log(e)

          EventBus.$emit('displaySnackbar', {
            display: true,
            text: this.$t('triggersHistories.errorTriggerRelaunch'),
            color: 'error',
          })
        } finally {
          this.loadingData = false
        }
      },
      getCallErrorHistory(callId) {
        return this.triggerHistory.executions_errors.filter(
          (execution_error) => execution_error.error_call_id == callId
        )
      },
    },
    computed: {
      sentDataBody() {
        try {
          return JSON.stringify(JSON.parse(this.triggerHistory.sent_data.body), null, 6)
        } catch (e) {
          console.error(e)
          return undefined
        }
      },
      sentAdditionalData() {
        try {
          return JSON.stringify(JSON.parse(this.triggerHistory.sent_data.additional_data), null, 6)
        } catch (e) {
          console.error(e)
          return undefined
        }
      },
      isMobile() {
        return this.$vuetify.breakpoint.width < 650
      },
      ...mapState(['isDark']),
      callsToRelaunch() {
        if (this.triggerHistory.trigger && Array.isArray(this.triggerHistory.trigger.trigger_calls)) {
          return this.triggerHistory.trigger.trigger_calls.filter((call) => call.retry === true).map((call) => call._id)
        } else {
          return []
        }
      },
      callsEligibleForRelaunch() {
        let callsEligibleForRelaunch = false
        if (this.triggerHistory.trigger && Array.isArray(this.triggerHistory.trigger.trigger_calls)) {
          callsEligibleForRelaunch = this.triggerHistory.trigger.trigger_calls.some(
            (call) => call.retry_origin_history_id === undefined
          )
        }
        return callsEligibleForRelaunch
      },
    },
    watch: {
      '$route.params.id'() {
        // location.reload()
        this.getTriggerHistory()
        this.checkConditions = false
        this.getEntityToRetry = false
      },
    },
  }
</script>
<style>
  .triggerHistoryTextArea textarea {
    scrollbar-color: #cca257 transparent;
  }
  .triggerHistoryTextArea textarea::-webkit-scrollbar {
    width: 12px;
  }

  .triggerHistoryTextArea textarea::-webkit-scrollbar-track {
    background-color: transparent;
  }

  .triggerHistoryTextArea textarea::-webkit-scrollbar-thumb {
    background-color: #cca257;
  }

  .triggerHistoryTextAreaDark textarea {
    scrollbar-color: #ffffff transparent;
  }
  .triggerHistoryTextAreaDark textarea::-webkit-scrollbar {
    width: 12px;
  }

  .triggerHistoryTextAreaDark textarea::-webkit-scrollbar-track {
    background-color: transparent;
  }

  .triggerHistoryTextAreaDark textarea::-webkit-scrollbar-thumb {
    background-color: #ffffff;
  }
</style>
